import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, Mail, MessageSquare, CheckCircle2, XCircle, Clock,
  Trash2, Loader2,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { to12h } from "../lib/format";
import { Button } from "../components/ui/button";
import { useConfirm } from "../hooks/useConfirm";
import { AdminLayout } from "../components/AdminLayout";
import { useI18n } from "../i18n/I18nContext";

const STATUS_TABS = [
  { value: "pending", label: "Pendentes", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { value: "approved", label: "Aprovadas", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { value: "scheduled", label: "Agendadas", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  { value: "rejected", label: "Recusadas", cls: "bg-rose-50 text-rose-800 border-rose-200" },
  { value: "", label: "Todas", cls: "bg-slate-100 text-slate-700 border-slate-200" },
];

const STATUS_MAP = {
  pending: { label: "Em análise", Icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Aprovada", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  scheduled: { label: "Agendada", Icon: Calendar, cls: "bg-blue-50 text-blue-800 border-blue-200" },
  rejected: { label: "Recusada", Icon: XCircle, cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function BookingRequests() {
  const { t } = useI18n();
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/booking-requests${params}`);
      setRequests(data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function updateStatus(req, newStatus) {
    let adminNotes = req.admin_notes || "";
    if (newStatus === "rejected") {
      const reason = window.prompt(t("Motivo da recusa (opcional, será mostrado ao cliente):"), "");
      if (reason === null) return;
      adminNotes = reason;
    }
    setActingId(req.id);
    try {
      await api.patch(`/booking-requests/${req.id}`, {
        status: newStatus,
        admin_notes: adminNotes,
      });
      toast.success(t("Solicitação atualizada"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActingId(null);
    }
  }

  async function remove(req) {
    const ok = await confirm({
      title: t("Excluir solicitação?"),
      description: `De ${req.client_name} em ${fmtDate(req.preferred_date)}. Esta ação não pode ser desfeita.`,
      confirmText: t("Excluir"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/booking-requests/${req.id}`);
      toast.success(t("Solicitação excluída"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function cleanupTestRequests() {
    const testItems = requests.filter(
      (r) =>
        /^test_/i.test(r.client_name || "") ||
        /example\.com$/i.test(r.client_email || "") ||
        /^test_book/i.test(r.client_email || ""),
    );
    if (testItems.length === 0) {
      toast.info(t("Nenhuma solicitação de teste encontrada"));
      return;
    }
    const ok = await confirm({
      title: `Excluir ${testItems.length} solicitação(ões) de teste?`,
      description: `Remove apenas registros com nome iniciando com "test_" ou e-mail @example.com. As reais (${requests.length - testItems.length}) permanecem.`,
      confirmText: t("Excluir testes"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await Promise.all(
        testItems.map((r) => api.delete(`/booking-requests/${r.id}`)),
      );
      toast.success(`${testItems.length} solicitação(ões) de teste removidas`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="booking-requests-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t("Solicitações de Agendamento")}</h1>
          <p className="text-slate-600 text-sm mt-1">
            {t("Pedidos enviados pelos clientes via portal.")}
          </p>
        </div>
        <button
          type="button"
          onClick={cleanupTestRequests}
          data-testid="booking-cleanup-tests"
          className="text-xs font-semibold text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
          title="Remove registros de teste (test_*, @example.com)"
        >
          🧹 {t("Limpar testes")}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="booking-status-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value || "all"}
            data-testid={`booking-tab-${tab.value || "all"}`}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              statusFilter === tab.value
                ? `${tab.cls} font-semibold`
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t("Carregando…")}
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="booking-empty">
          {t("Nenhuma solicitação encontrada.")}
        </div>
      ) : (
        <ul className="space-y-3">
          {requests.map((r) => {
            const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
            const StIcon = st.Icon;
            const busy = actingId === r.id;
            return (
              <li
                key={r.id}
                data-testid={`booking-request-${r.id}`}
                className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3"
              >
                <div className="flex flex-wrap items-start gap-3 justify-between">
                  <div>
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      {r.client_name}
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${st.cls}`}>
                        <StIcon size={11} /> {t(st.label)}
                      </span>
                    </div>
                    {r.client_email && (
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Mail size={11} /> {r.client_email}
                      </div>
                    )}
                  </div>
                  <button
                    data-testid={`booking-delete-${r.id}`}
                    onClick={() => remove(r)}
                    className="text-rose-500 hover:text-rose-700 p-1.5 rounded hover:bg-rose-50"
                    title="Excluir"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("Data preferida")}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Calendar size={13} className="text-blue-600" />
                      <span className="font-semibold">{fmtDate(r.preferred_date)}</span>
                      {r.preferred_time && <span className="text-slate-600">• {to12h(r.preferred_time)}</span>}
                    </div>
                  </div>
                  {r.frequency && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("Frequência")}</div>
                      <div className="mt-0.5">{r.frequency}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("Enviado em")}</div>
                    <div className="mt-0.5 text-slate-600 text-xs">{fmtDate(r.created_at)}</div>
                  </div>
                </div>

                {r.notes && (
                  <div className="text-sm text-slate-700 bg-slate-50 rounded p-2 flex items-start gap-2">
                    <MessageSquare size={13} className="mt-0.5 text-slate-400 flex-shrink-0" />
                    <span className="whitespace-pre-line">{r.notes}</span>
                  </div>
                )}

                {r.admin_notes && (
                  <div className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded p-2">
                    <strong>{t("Sua resposta:")}</strong> {r.admin_notes}
                  </div>
                )}

                {/* Actions */}
                {r.status === "pending" && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      data-testid={`booking-approve-${r.id}`}
                      onClick={() => updateStatus(r, "approved")}
                      disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle2 size={14} className="mr-1" /> {t("Aprovar")}
                    </Button>
                    <Button
                      data-testid={`booking-schedule-${r.id}`}
                      onClick={() => updateStatus(r, "scheduled")}
                      disabled={busy}
                      variant="outline"
                    >
                      <Calendar size={14} className="mr-1" /> {t("Marcar como Agendada")}
                    </Button>
                    <Button
                      data-testid={`booking-reject-${r.id}`}
                      onClick={() => updateStatus(r, "rejected")}
                      disabled={busy}
                      variant="outline"
                      className="text-rose-700 border-rose-200 hover:bg-rose-50"
                    >
                      <XCircle size={14} className="mr-1" /> {t("Recusar")}
                    </Button>
                  </div>
                )}
                {r.status === "approved" && (
                  <div className="flex gap-2 pt-1">
                    <Button
                      data-testid={`booking-schedule-${r.id}`}
                      onClick={() => updateStatus(r, "scheduled")}
                      disabled={busy}
                    >
                      <Calendar size={14} className="mr-1" /> {t("Marcar como Agendada")}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      </div>
    </AdminLayout>
  );
}
